import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErrorMessage } from "@/api/axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Globe } from "lucide-react";

export function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(() => Boolean(token));
  const [valid, setValid] = useState(false);
  const [error, setError] = useState(() =>
    token ? "" : "Неверная ссылка"
  );
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    if (!token) return;
    api.get(`/api/invite/${token}`)
      .then((res) => {
        if (res.data.valid) {
          setValid(true);
        } else {
          setError(res.data.error || "Ссылка недействительна или истекла");
        }
      })
      .catch(() => {
        setError("Ошибка проверки ссылки");
      })
      .finally(() => {
        setChecking(false);
      });
  }, [token]);

  const register = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!username.trim() || !password.trim()) return;
    if (password !== confirmPassword) {
      setError("Пароли не совпадают");
      return;
    }
    if (password.length < 6) {
      setError("Пароль должен быть не менее 6 символов");
      return;
    }

    setRegistering(true);
    try {
      const formData = new URLSearchParams();
      formData.append("username", username.trim());
      formData.append("password", password);
      formData.append("invite_token", token || "");
      if (email.trim()) {
        formData.append("email", email.trim());
      }
      const res = await api.post("/api/auth/register", formData, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (res.data.success) {
        navigate("/login");
      } else {
        setError(res.data.error || "Ошибка регистрации");
      }
    } catch (e: unknown) {
      setError(apiErrorMessage(e, "Ошибка регистрации"));
    } finally {
      setRegistering(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-sm border-slate-800 bg-slate-900">
          <CardContent className="pt-6 text-center">
            <Globe className="h-10 w-10 text-slate-500 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-slate-200 mb-2">Ошибка</h2>
            <p className="text-sm text-slate-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-sm border-slate-800 bg-slate-900">
        <CardHeader>
          <CardTitle className="text-center text-slate-200">Регистрация</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={register} className="space-y-3">
            <Input
              id="invite-username"
              name="username"
              type="text"
              autoComplete="off"
              placeholder="Логин"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="bg-slate-950 border-slate-700"
              required
            />
            <Input
              id="invite-email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="Email (необязательно)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-slate-950 border-slate-700"
            />
            <Input
              id="invite-password"
              name="password"
              type="password"
              autoComplete="new-password"
              placeholder="Пароль"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-slate-950 border-slate-700"
              required
            />
            <Input
              id="invite-confirm"
              name="confirm_password"
              type="password"
              autoComplete="new-password"
              placeholder="Подтвердите пароль"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="bg-slate-950 border-slate-700"
              required
            />
            {error && <div className="text-sm text-red-400">{error}</div>}
            <Button type="submit" className="w-full" disabled={registering}>
              {registering ? <Loader2 className="h-4 w-4 animate-spin" /> : "Зарегистрироваться"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
